import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import isEmail from "validator/lib/isEmail";
import { toast } from "react-hot-toast";
import { AiOutlineLeft } from "react-icons/ai";
import { apiInstance } from "../config/apiConfig";

import "../sass/homepage.scss";
import "../sass/signup.scss";

/* deterministic toast-id helper */
const toastKey = (action, email) => `${action}-${email}`;

export default function Signup() {
  const nav = useNavigate();

  /* form state */
  const [emailId,   setEmailId]   = useState("");
  const [otp,       setOtp]       = useState(Array(6).fill(""));
  const [otpSent,   setOtpSent]   = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const [firstName, setFirstName] = useState("");
  const [lastName,  setLastName]  = useState("");
  const [password,  setPassword]  = useState("");

  /* ───── SEND / RESEND OTP ───── */
  const handleSendOtp = () => {
    if (!isEmail(emailId)) return toast.error("Enter a valid e-mail");

    const id = toastKey("send", emailId);
    toast
      .promise(
        apiInstance.post("/api/otp/send-otp", { email: emailId }),
        {
          loading : "Sending OTP…",
          success : "OTP sent — check your mailbox",
          error   : err => err?.response?.data?.message || "Failed to send OTP",
        },
        { id }
      )
      .then(() => {
        setOtpSent(true);
        setOtp(Array(6).fill(""));
        startResendCountdown();
      })
      .catch(() => {/* error already shown */});
  };

  /* 90-second resend timer */
  const startResendCountdown = () => {
    setResendTimer(90);
    const tick = setInterval(() => {
      setResendTimer(t => (t <= 1 ? (clearInterval(tick), 0) : t - 1));
    }, 1000);
  };

  /* ───── VERIFY OTP ───── */
  const handleVerifyOtp = () => {
    const code = otp.join("");
    if (code.length !== 6) return toast.error("Enter the 6-digit OTP");

    const id = toastKey("verify", emailId);
    toast
      .promise(
        apiInstance.post("/api/otp/verify-otp", { email: emailId, otp: code }),
        {
          loading : "Verifying…",
          success : "OTP verified!",
          error   : err => err?.response?.data?.message || "Invalid / expired OTP",
        },
        { id }
      )
      .then(() => {
        setOtpVerified(true);
        toast.success("Great! Fill in your details to finish sign-up.");
      })
      .catch(() => {/* toast displayed */});
  };

  /* ───── REGISTER USER ───── */
  const handleRegister = () => {
    if (!firstName || !lastName || !password)
      return toast.error("Please complete all fields");

    const id = toastKey("register", emailId);
    toast
      .promise(
        apiInstance.post("/api/user/register", {
          firstName, lastName, emailId, password,
        }),
        {
          loading : "Creating account…",
          success : "Registration complete — welcome aboard!",
          error   : err => err?.response?.data?.message || "Registration failed",
        },
        { id }
      )
      .then(() => nav("/dashboard"))
      .catch(() => {/* toast displayed */});
  };

  /* OTP input helper */
  const handleOtpChange = (val, idx) => {
    const next = [...otp];
    if (/^\d{6}$/.test(val)) next.splice(0, 6, ...val.slice(0, 6));
    else next[idx] = val.slice(-1);
    setOtp(next);
  };

  /* UI */
  return (
    <section id="body">
      <div className="signup">
        <div className="flex justify-center items-center min-h-screen">
          <div className="card w-full max-w-md bg-base-100 shadow-xl">
            <div className="card-body">
              {/* header */}
              <div className="button-container">
                <button onClick={() => nav("/login")} className="back-button text-xl font-bold icon-align">
                  <AiOutlineLeft /> Back
                </button>
                <button onClick={() => nav("/login")} className="signup-button">
                  Login
                </button>
              </div>

              {/* step 1 – e-mail */}
              {!otpSent && (
                <>
                  <label className="label"><h2>Email Address</h2></label>
                  <input
                    type="email"
                    className="input input-bordered w-full"
                    value={emailId}
                    onChange={e => setEmailId(e.target.value)}
                  />
                  <button className="secondary-button mt-6" onClick={handleSendOtp}>
                    Send OTP
                  </button>
                </>
              )}

              {/* step 2 – OTP */}
              {otpSent && !otpVerified && (
                <>
                  <label className="label">Enter OTP</label>
                  <div className="otp-inputs-container">
                    {otp.map((d, i) => (
                      <input
                        key={i}
                        type="text"
                        maxLength="1"
                        value={d}
                        onChange={e => handleOtpChange(e.target.value, i)}
                        className="otp-input w-10 text-center"
                      />
                    ))}
                  </div>
                  <button className="secondary-button mt-6" onClick={handleVerifyOtp}>
                    Verify OTP
                  </button>

                  {resendTimer > 0 ? (
                    <p className="text-center mt-4">Wait {resendTimer}s to resend</p>
                  ) : (
                    <button className="secondary-button mt-4" onClick={handleSendOtp}>
                      Resend OTP
                    </button>
                  )}
                </>
              )}

              {/* step 3 – personal info */}
              {otpVerified && (
                <>
                  <input
                    type="text"
                    placeholder="First Name"
                    className="input input-bordered"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="Last Name"
                    className="input input-bordered"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                  />
                  <input
                    type="password"
                    placeholder="Password"
                    className="input input-bordered"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                  />
                  <button className="secondary-button mt-6" onClick={handleRegister}>
                    Register
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
